import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import '../../css/Signup.css';

import CustomAlert from '../../components/CustomAlert';
import { submitSignupForm, fetchCmsLinks } from '../../api/api';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // 👁️ Import eye icons
import { useTranslation } from '../../hooks/useTranslation';
import Footer from '../../components/Footer';

const SignupForm = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [showPassword, setShowPassword] = useState(false); // 👁️ toggle for password
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // 👁️ toggle for confirm password
  const [loading, setLoading] = useState(false); // ⏳ Loader state

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
    marketingOptIn: false,
  });

  const lang = useSelector((state) => state.user.lang) || 'EN';
  const [cmsLinks, setCmsLinks] = useState({});

  useEffect(() => {
    fetchCmsLinks(lang)
      .then((links) => {
        setCmsLinks(links);
      })
      .catch((err) => {
        logger.error('Failed to load CMS links:', err);
      });
  }, [lang]);

  const termsUrl = cmsLinks.terms?.url;
  const privacyUrl = cmsLinks.privacy?.url;

  let termsText = t('registration_terms_privacy_checkbox');

  termsText = termsText.replace('{terms_url}', termsUrl).replace('{privacy_url}', privacyUrl);

  let marketingText = t('registration_marketing_checkbox');

  let validationErrorText = t('registration_terms_privacy_required');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Basic validations
    if (!formData.firstName) {
      CustomAlert.error(t('required_field'), t('first_name_is_required'));
      return;
    }
    if (!formData.lastName) {
      CustomAlert.error(t('required_field'), t('last_name_is_required'));
      return;
    }
    if (!formData.email) {
      CustomAlert.error(t('required_field'), t('email_is_required'));
      return;
    }
    if (!formData.password) {
      CustomAlert.error(t('required_field'), t('password_is_required'));
      return;
    }
    if (!formData.confirmPassword) {
      CustomAlert.error(t('required_field'), t('confirm_password_is_required'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      CustomAlert.error(t('required_field'), t('passwords_do_not_match'));
      return;
    }
    if (!formData.termsAccepted) {
      CustomAlert.error(t('required_field'), validationErrorText);
      return;
    }

    try {
      setLoading(true);
      const response = await submitSignupForm({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        terms_accepted: formData.termsAccepted,
        marketing_opt_in: formData.marketingOptIn,
      });
      CustomAlert.success(t('success'), t(response.message));
      navigate('/login', { replace: true });
    } catch (error) {
      CustomAlert.error(t('Error'), t(error?.response?.data?.message || 'signup_failed'));
      logger.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          flex: '1 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div className="login-wrapper">
          <div className="login-image">
            <img src="/img/RocketBPM_rocket_logo.png" alt="Logo" className="login-logo" />
          </div>
          <h2>{t('join_rocketbpmcom')}</h2>
          <form className="login-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={t('First_Name')}
              className="login-input"
            />
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={t('Last_Name')}
              className="login-input"
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('Email')}
              className="login-input"
            />
            {/* 👁️ Password input with eye toggle */}
            <div className="password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('password')}
                className="login-input password-input"
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* 👁️ Confirm Password input with eye toggle */}
            <div className="password-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('confirm_password')}
                className="login-input password-input"
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Consent Checkboxes */}
            <div className="consent-checkbox-container">
              <label className="consent-checkbox-label">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted || false}
                  onChange={handleCheckboxChange}
                  className="consent-checkbox-input"
                />
                <span
                  className="consent-checkbox-text"
                  dangerouslySetInnerHTML={{ __html: termsText }}
                />
              </label>

              <label className="consent-checkbox-label">
                <input
                  type="checkbox"
                  name="marketingOptIn"
                  checked={formData.marketingOptIn || false}
                  onChange={handleCheckboxChange}
                  className="consent-checkbox-input"
                />
                <span className="consent-checkbox-text">{marketingText}</span>
              </label>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? `${t('please_wait') || t('loading') || 'Please wait'}...` : t('sign_up')}
            </button>
            <p>
              {t('already_a_member')}
              <button className="btn_form" type="button" onClick={() => navigate('/login')}>
                {t('sign_in')}
              </button>
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SignupForm;
